"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { useSpoonerIndex } from "@/components/tools/word-games/use-spooner-index";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  MAX_WORDS,
  type NewWord,
  type SpoonerIndex,
  type Spoonerism,
  spoonerisms,
} from "@/lib/tool-kit/word-games/spoonerism";

/** English strings. A translated content file overrides these through `ui`. */
export const SPOONERISM_GENERATOR_STRINGS = {
  inputLabel: "Phrases, one per line",
  inputHint:
    "Two to twelve words work best. Every pair of words up to three places apart is tried.",
  inputPlaceholder: "a crushing blow",
  realOnly: "Only pairs where both new words are real words",
  tryLabel: "Try:",
  examples: [
    "a crushing blow",
    "a lighting of fire",
    "wave the sails",
    "well-oiled bicycle",
  ],
  emptyPrompt:
    "Type a phrase to swap the opening sounds of its words. Use two or more words that begin with different consonant sounds.",
  loading: "Loading the pronunciation data (once per visit)…",
  loadError:
    "Could not load the pronunciation data. Check your connection and try again.",
  retry: "Try again",
  heading: "Spoonerisms",
  lineHeading: "“{line}”",
  count: "one:{count} spoonerism|other:{count} spoonerisms",
  noPairs:
    "No spoonerism for this phrase: it needs two words that start with different consonant sounds.",
  noRealPairs:
    "No pair gives two real words. Clear the filter to see every swap.",
  unknown: "Not in the pronunciation dictionary, so left out: {words}.",
  truncated: "Only the first {count} words of this phrase were read.",
  bothReal: "Both are real words",
  oneReal: "One is a real word",
  noneReal: "Not real words",
  swap: "{from} → {to}",
  alsoSounds: "Also sounds like: {words}",
  guessed: "spelling guessed",
  soundOnly: "no spelling, sound only",
  showMore: "Show more",
  copyAll: "Copy all",
  copied: "Copied!",
  note: "Sounds are General American English from the CMU Pronouncing Dictionary. “Real word” means a word-game list (ENABLE) has a word pronounced exactly that way; the page cannot judge whether the result is funny. Guessed spellings are only a starting point: say them aloud.",
} as const;

type Strings = typeof SPOONERISM_GENERATOR_STRINGS;

const FIELD_ID = "spoon-phrases";
const MAX_LINES = 6;
const MAX_LINE_LENGTH = 120;
const PAGE = 8;
const SWAPPED = "rounded bg-primary/15 px-0.5 font-semibold text-foreground";

function nonEmptyLines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim().slice(0, MAX_LINE_LENGTH))
    .filter((line) => line.length > 0)
    .slice(0, MAX_LINES);
}

function realLabel(count: number, strings: Strings): string {
  if (count === 2) {
    return strings.bothReal;
  }
  return count === 1 ? strings.oneReal : strings.noneReal;
}

function WordLine({
  from,
  word,
  strings,
}: {
  from: string;
  word: NewWord;
  strings: Strings;
}) {
  const note =
    (word.kind === "guess" && strings.guessed) ||
    (word.kind === "sound" && strings.soundOnly) ||
    "";
  return (
    <li>
      <span className="font-medium text-foreground">
        {t(strings.swap, { from, to: word.text })}
      </span>
      {word.ipa && word.kind !== "sound" ? (
        <span className={HINT}> /{word.ipa}/</span>
      ) : null}
      {note ? <span className={HINT}> ({note})</span> : null}
      {word.alternates.length > 0 ? (
        <span className={HINT}>
          {" · "}
          {t(strings.alsoSounds, { words: word.alternates.join(", ") })}
        </span>
      ) : null}
    </li>
  );
}

function Result({ item, strings }: { item: Spoonerism; strings: Strings }) {
  return (
    <li className={`${CARD} space-y-2`}>
      <p className="text-foreground text-xl leading-snug">
        {item.parts.map((part, position) =>
          part.swapped ? (
            <mark
              className={SWAPPED}
              // biome-ignore lint/suspicious/noArrayIndexKey: a part's identity is its position in the phrase
              key={position}
            >
              {part.text}
            </mark>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: a part's identity is its position in the phrase
            <span key={position}>{part.text}</span>
          )
        )}
      </p>
      <ul className="space-y-0.5 text-sm">
        <WordLine from={item.from[0]} strings={strings} word={item.first} />
        <WordLine from={item.from[1]} strings={strings} word={item.second} />
      </ul>
      <p
        className={`font-medium text-xs ${item.realCount === 2 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}
      >
        {realLabel(item.realCount, strings)}
      </p>
    </li>
  );
}

function LineResults({
  line,
  index,
  realOnly,
  single,
  strings,
}: {
  line: string;
  index: SpoonerIndex;
  realOnly: boolean;
  single: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [shown, setShown] = useState(PAGE);
  const set = useMemo(() => spoonerisms(line, index), [line, index]);
  const items = realOnly
    ? set.results.filter((item) => item.realCount === 2)
    : set.results;
  let empty = "";
  if (set.results.length === 0) {
    empty = strings.noPairs;
  } else if (items.length === 0) {
    empty = strings.noRealPairs;
  }
  return (
    <section className="space-y-3">
      {single ? null : (
        <h3 className={WIDGET_H3}>{t(strings.lineHeading, { line })}</h3>
      )}
      {set.unknown.length > 0 ? (
        <p className={HINT}>
          {t(strings.unknown, { words: set.unknown.join(", ") })}
        </p>
      ) : null}
      {set.truncated ? (
        <p className={HINT}>
          {t(strings.truncated, { count: fmt(MAX_WORDS) })}
        </p>
      ) : null}
      {empty ? <Notice>{empty}</Notice> : null}
      {items.length > 0 ? (
        <>
          <p className={HINT}>
            {tPlural(strings.count, locale, items.length, {
              count: fmt(items.length),
            })}
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {items.slice(0, shown).map((item) => (
              <Result
                item={item}
                key={`${item.from.join("|")}-${item.distance}-${item.phrase}`}
                strings={strings}
              />
            ))}
          </ul>
          {items.length > shown ? (
            <button
              className={BUTTON_SMALL}
              onClick={() => setShown((count) => count + PAGE)}
              type="button"
            >
              {strings.showMore}
            </button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

export default function SpoonerismGeneratorWidget() {
  const strings = useToolUi(SPOONERISM_GENERATOR_STRINGS);
  const [text, setText] = useState("");
  const [realOnly, setRealOnly] = useState(false);
  const lines = useMemo(() => nonEmptyLines(text), [text]);
  const { index, status, retry } = useSpoonerIndex(lines.length > 0);
  const copyText = useMemo(() => {
    if (!index) {
      return "";
    }
    return lines
      .flatMap((line) => spoonerisms(line, index).results)
      .filter((item) => !realOnly || item.realCount === 2)
      .map((item) => item.phrase)
      .join("\n");
  }, [index, lines, realOnly]);

  return (
    <>
      <TextAreaField
        hint={strings.inputHint}
        id={FIELD_ID}
        label={strings.inputLabel}
        onChange={(value) => setText(value.slice(0, 700))}
        placeholder={strings.inputPlaceholder}
        prose
        rows={3}
        value={text}
      />
      <Checkbox
        checked={realOnly}
        label={strings.realOnly}
        onChange={setRealOnly}
      />
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className={HINT}>{strings.tryLabel}</span>
        {strings.examples.map((example) => (
          <button
            className="font-medium text-primary underline-offset-4 hover:underline"
            key={example}
            onClick={() => setText(example)}
            type="button"
          >
            {example}
          </button>
        ))}
      </p>
      {lines.length === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {index && lines.length > 0 ? (
        <div aria-live="polite" className="space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className={WIDGET_H2}>{strings.heading}</h2>
            <CopyButton
              copiedLabel={strings.copied}
              disabled={copyText === ""}
              label={strings.copyAll}
              small
              text={copyText}
            />
          </div>
          {lines.map((line) => (
            <LineResults
              index={index}
              key={line}
              line={line}
              realOnly={realOnly}
              single={lines.length === 1}
              strings={strings}
            />
          ))}
        </div>
      ) : null}
      <p className={HINT}>{strings.note}</p>
    </>
  );
}
